<template>
  <div :class="rangeInputClass">
    <div
      v-if="showHistogram"
      class="histogram-wrap"
    >
      <ChartByHistogram
        :histogram
        :max
        :min
      />
    </div>
    <div
      ref="wrap"
      class="slider-wrap"
      @click="onClick"
      @touchstart="onTouchstart"
    >
      <div class="track" />
      <div
        class="gap"
        :style="gapStyle"
      />
      <div
        v-for="i in 2"
        :key="i"
        :class="thumbClass"
        :style="thumbStyle[i - 1]"
        @mousedown="onMousedown(i - 1, $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import ChartByHistogram from '../ui/ChartByHistogram.vue';

  interface Props {
    min: number;
    max: number;
    histogram: Record<string, number>;
    value: Array<number>;
    isModal: boolean;
  }

  interface Emits {
    (e: 'input', value: Array<number>): void;
  }

  const emit = defineEmits<Emits>();

  const props = defineProps<Props>();

  const dragging = ref<null | number>(null);
  const wrap = ref<HTMLElement | null>(null);
  const touchId = ref<number | null>(null);

  const showHistogram = computed(() => props.histogram && Object.values(props.histogram).some((x) => x > 0));
  const rangeInputClass = computed(() => ['range-slider', { 'show-histogram': showHistogram.value }]);
  const thumbClass = computed(() => [{ thumb: true }]);

  const thumbStyle = computed(() => {
    if (props.min === props.max) {
      return [{ left: '0' }, { left: '100%' }];
    }

    return [{ left: `${numberToPercent(numbers.value[0])}%` }, { left: `${numberToPercent(numbers.value[1])}%` }];
  });

  const gapStyle = computed(() => {
    const rangeOutOfOrder = numbers.value[0] > numbers.value[1];

    if (props.min === props.max) {
      return { left: 0, right: 0 };
    }

    if (rangeOutOfOrder) {
      return {
        left: `${numberToPercent(numbers.value[1])}%`,
        right: `${100 - numberToPercent(numbers.value[0])}%`,
      };
    }

    return {
      left: `${numberToPercent(numbers.value[0])}%`,
      right: `${100 - numberToPercent(numbers.value[1])}%`,
    };
  });

  const numbers = computed(() => {
    let [firstValue, secondValue] = props.value;

    if (firstValue < props.min) {
      firstValue = props.min;
    }
    if (firstValue > props.max) {
      firstValue = props.max;
    }
    if (secondValue < props.min) {
      secondValue = props.min;
    }
    if (secondValue > props.max) {
      secondValue = props.max;
    }

    return [firstValue, secondValue];
  });

  const setDragging = (value: number | null) => {
    dragging.value = value;
  };

  const numberToPercent = (number: number) => {
    const la = Math.log(props.min);
    const lb = Math.log(props.max);
    const ln = Math.log(number);

    return ((ln - la) / (lb - la)) * 100;
  };

  const pxToNumber = (px: number) => {
    const { left, width } = wrap.value?.getBoundingClientRect() as DOMRect;
    const percent = (px - left) / width;
    const number = props.max ** percent * props.min ** (1 - percent);

    return Math.round(number);
  };

  const closestHandle = (px: number) => {
    const { left, width } = wrap.value?.getBoundingClientRect() as DOMRect;
    const percent = ((px - left) / width) * 100;
    const p0 = numberToPercent(numbers.value[0]);
    const p1 = numberToPercent(numbers.value[1]);
    const dist0 = Math.abs(p0 - percent);
    const dist1 = Math.abs(p1 - percent);
    if (percent < p0) {
      return 0;
    }

    return dist0 < dist1 ? 0 : 1;
  };

  const onHandlePosChange = (idx: number, px: number) => {
    if (props.min === props.max) {
      return;
    }

    const value = numbers.value.slice();
    value[idx] = pxToNumber(px);

    if (value[0] > value[1] && dragging.value !== null) {
      value.reverse();
      dragging.value = idx ? 0 : 1;
    }

    value[0] = Math.max(props.min, value[0]);
    value[1] = Math.min(props.max, value[1]);

    // Эмитим изменения только если значение реально изменились
    if (value[0] !== props.value[0] || value[1] !== props.value[0]) {
      emit('input', value);
    }
  };

  const onClick = (event: MouseEvent) => {
    const idx = closestHandle(event.pageX);
    onHandlePosChange(idx, event.pageX);
  };

  const onTouchstart = (event: TouchEvent) => {
    if (event.cancelable) {
      const touch = event.changedTouches[0];
      dragging.value = closestHandle(touch.pageX);
      onHandlePosChange(dragging.value, touch.pageX);
      touchId.value = event.targetTouches[0].identifier;
      event.preventDefault();
    }
  };

  const onMousedown = (index: number, event: MouseEvent) => {
    setDragging(index);
    event.preventDefault();
  };

  const onMouseup = () => {
    setDragging(null);
  };

  const onMousemove = (event: MouseEvent) => {
    if (dragging.value !== null) {
      if (event.buttons !== 1) {
        setDragging(null);
      } else {
        onHandlePosChange(dragging.value, event.pageX);
      }
    }
  };

  const onTouchmove = (event: TouchEvent) => {
    if (dragging.value !== null) {
      for (const touch of event.changedTouches) {
        if (touch.identifier === touchId.value) {
          onHandlePosChange(dragging.value, touch.pageX);
          break;
        }
      }
    }
  };

  const onTouchend = (event: TouchEvent) => {
    if (dragging.value !== null) {
      for (const touch of event.changedTouches) {
        if (touch.identifier === touchId.value) {
          setDragging(null);
          break;
        }
      }
    }
  };

  onMounted(() => {
    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', onMouseup);
    document.addEventListener('touchmove', onTouchmove);
    document.addEventListener('touchend', onTouchend);
  });

  onUnmounted(() => {
    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    document.removeEventListener('touchmove', onTouchmove);
    document.removeEventListener('touchend', onTouchend);
  });
</script>

<style scoped lang="scss">
  .range-slider {
    --handle-size: 24px;
    position: relative;
    padding: 0 24px 0 0;

    &:not(.show-histogram) {
      padding-top: 10px;
    }
  }

  .histogram-wrap {
    margin: 0 1px -28px;
    margin-right: -24px;
  }

  .slider-wrap {
    position: relative;
    height: var(--handle-size);
    margin: 16px 0 0;
  }

  .track,
  .gap {
    position: absolute;
    top: 50%;
    height: 4px;
    transform: translateY(-50%);
  }

  .track {
    position: relative;
    left: 0;
    margin-right: -24px;
    border-radius: 2px;
    background: var(--alias-color-background-subtler);

    &:before {
      content: '';
      position: absolute;
      right: 0;
      bottom: 100%;
      left: 0;
      height: 16px;
      background: linear-gradient(180deg, transparent 0%, var(--alias-color-background-default) 100%);
    }
  }

  .gap {
    margin-left: 2px;
    background-color: var(--brand-color);
  }

  .thumb {
    position: absolute;
    top: 0;
    box-sizing: border-box;
    width: 24px;
    height: 24px;
    border: 4px solid var(--brand-color);
    border-radius: 50%;
    background: var(--alias-color-background-default);
    cursor: grab;

    &:active {
      cursor: grabbing;
    }

    &.dragging {
      z-index: 1;
    }
  }
</style>
